import {
  ALGOLIA_INDEX,
  ALGOLIA_SYNC,
  type IAlgoliaSyncData,
  type IAlgoliaSyncVar,
} from '@quesmed/types/resolvers/mutation/admin';
import type { GraphQLError } from 'graphql/error/GraphQLError';
import { useEffect, useMemo, useState } from 'react';
import { FaCheckCircle } from 'react-icons/fa';
import { FaDownload } from 'react-icons/fa6';
import { LiaSyncSolid } from 'react-icons/lia';
import { MdError } from 'react-icons/md';
import { useDisclosure } from '@/core/hooks';
import { Heading, OperationStates } from '@/core/molecules';
import { Alert, Button, Confirm, cn, Tooltip } from '@/core/ui';
import { useMutation } from '@/libs';
import { useGlobal } from '@/store';
import { fileDownload } from '@/utils/dom';

const ActionButton = ({
  index,
  onClick,
  loading,
  download,
}: {
  index: ALGOLIA_INDEX;
  onClick: () => void;
  loading?: boolean;
  download?: () => void;
}) => {
  const completedList = useGlobal.use.completedList();

  const state = useMemo(() => {
    if (loading) {
      return 'loading';
    }

    if (Object.hasOwn(completedList, index)) {
      return completedList[index] ? 'success' : 'error';
    }
    return 'idle';
  }, [loading, completedList, index]);

  const mapping = {
    idle: { text: 'Start Indexing!', icon: <LiaSyncSolid className={'w-6 h-6'} /> },
    success: { text: 'Indexing Completed!', icon: <FaCheckCircle className={'w-6 h-6 text-success-500'} /> },
    error: { text: 'Error in Indexing', icon: <MdError className={'w-6 h-6 text-danger-500'} /> },
    loading: {
      text: 'Indexing in progress!',
      icon: <LiaSyncSolid className={'w-6 h-6 text-primary-800 animate-spin'} />,
    },
  };

  return (
    <div className='flex space-x-4 items-center'>
      {state === 'error' && download && (
        <Button onPress={download} color='danger' className='' size='sm'>
          <FaDownload />
          Error Log
        </Button>
      )}
      <Tooltip placement='left' content={mapping[state].text}>
        <button onClick={onClick} type='button' className={cn('p-2.5 rounded-full hover:bg-primary-100')}>
          {mapping[state].icon}
        </button>
      </Tooltip>
    </div>
  );
};

const ReIndex = () => {
  const [algoliaSync, { loading }] = useMutation<IAlgoliaSyncData, IAlgoliaSyncVar>(ALGOLIA_SYNC);
  const { isOpen, onOpenChange, onOpen } = useDisclosure();
  const setCompletedList = useGlobal.use.setCompletedList();
  const [selectedIndex, setIndex] = useState<ALGOLIA_INDEX>();
  const [errorData, setErrorData] = useState<readonly GraphQLError[] | null>(null);

  useEffect(() => {
    return () => {
      setCompletedList('', false, true);
    };
  }, []);

  const handleOnClick = (index: ALGOLIA_INDEX) => {
    onOpen();
    setIndex(index);
  };

  const downloadError = () => {
    try {
      fileDownload('ERROR.json', errorData);
    } finally {
      setErrorData(null);
    }
  };

  const onSubmit = async () => {
    try {
      if (!selectedIndex) {
        return;
      }
      const { data } = await algoliaSync({ variables: { index: selectedIndex } });
      if (data?.admin?.algoliaSync) {
        setCompletedList(selectedIndex, true);
      } else {
        setCompletedList(selectedIndex, false);
      }
    } finally {
      setIndex(undefined);
    }
  };

  return (
    <>
      <Confirm
        onConfirm={onSubmit}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        title={`Re-Index ${selectedIndex?.replace('rebrand_', ' ')}`}
        message='Are you sure want to proceed?'
      />
      <div className='flex flex-col space-y-2 justify-center w-full items-center'>
        <div className='bg-background rounded-lg shadow-md w-full container z-[2]'>
          <Heading title='Re-Index Algolia' />
          <div className='p-6 md:p-10'>
            <div className='grid grid-cols-4 gap-y-4 md:gap-y-0 gap-x-6 mb-8'>
              <Alert
                type='warn'
                className='col-span-4 md:col-span-3'
                message='Please read below before proceeding!'
                list={[
                  'Please Make sure correct DB storage is selected!',
                  'Perform this only during low activity hours',
                  'Perform only single indexing at a time',
                  'Single operation maybe take around 5 minutes',
                  'Click on the sync icon to start re-indexing',
                  'Right section shows different states of operation',
                  'In case of any error please contact quesmed team as it will have flushed data before re-index',
                ]}
              />
              <OperationStates
                className={cn(
                  'col-span-4 md:col-span-1 rounded-md border border-divider p-5',
                  'flex flex-col justify-between space-y-3',
                )}
              />
            </div>
            <div className='flex flex-col justify-center'>
              {Object.values(ALGOLIA_INDEX).map((value, index) => (
                <div
                  className='flex justify-between items-center border-b py-1 border-divider'
                  key={value as string}
                >
                  <span className='text-sm md:text-base capitalize'>
                    {index + 1}.&nbsp;&nbsp;{(value as string).replace('rebrand_', ' ')}
                  </span>
                  <ActionButton
                    download={errorData ? downloadError : undefined}
                    loading={loading && value === selectedIndex}
                    onClick={() => handleOnClick(value)}
                    index={value}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReIndex;
